import { useState } from 'react'
import { Plus } from 'lucide-react'
import { useVisitTypes } from '@/hooks/useVisitTypes'
import { AdminRoute } from '@/components/layout/AdminRoute'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { formatVisitTypeCategory } from '@/lib/format'
import LoadingPage from '@/components/shared/LoadingPage'
import ErrorState from '@/components/shared/ErrorState'
import EmptyState from '@/components/shared/EmptyState'
import { VisitTypeModal } from '@/components/visit-types/VisitTypeModal'
import type { VisitType } from '@/types/settings'

function formatDollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`
}

function VisitTypeManagementContent() {
  const { data: visitTypes, isLoading, error, refetch } = useVisitTypes()
  const [modalState, setModalState] = useState<{ open: boolean; visitType: VisitType | null }>({
    open: false,
    visitType: null,
  })

  if (isLoading) {
    return <LoadingPage variant="list" />
  }

  if (error) {
    return <ErrorState message="Failed to load visit types." onRetry={() => refetch()} />
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Visit Types</h1>
        <Button onClick={() => setModalState({ open: true, visitType: null })}>
          <Plus className="h-4 w-4 mr-1" /> Add Visit Type
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Configured Visit Types</CardTitle>
        </CardHeader>
        <CardContent>
          {!visitTypes || visitTypes.length === 0 ? (
            <EmptyState title="No visit types configured" />
          ) : (
            <div className="space-y-2">
              {visitTypes.map((vt) => (
                <button
                  key={vt.id}
                  type="button"
                  className="flex w-full items-center justify-between rounded-lg border p-4 text-left hover:bg-slate-50 transition-colors"
                  onClick={() => setModalState({ open: true, visitType: vt })}
                >
                  <div className="flex flex-col gap-1">
                    <span className="text-sm font-medium text-slate-900">{vt.name}</span>
                    <span className="text-xs text-slate-500">{vt.provider_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">{formatVisitTypeCategory(vt.category)}</Badge>
                    <span className="text-sm font-medium text-slate-700">{formatDollars(vt.price_cents)}</span>
                    <Badge variant={vt.is_active ? 'default' : 'secondary'}>
                      {vt.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <VisitTypeModal
        visitType={modalState.visitType}
        open={modalState.open}
        onClose={() => setModalState({ open: false, visitType: null })}
      />
    </div>
  )
}

export default function VisitTypeManagementPage() {
  return (
    <AdminRoute>
      <VisitTypeManagementContent />
    </AdminRoute>
  )
}
